// ---------------------------------------------------------------------------
// The reveal. Dictionary Corner, and the one screen the whole round pays off in.
//
// **The letters reveal is one list again, and this comment used to argue the
// other way.** Two shapes have been tried and both were right about something.
// A stack of frames, one appended per released player, never moved anything
// already on screen and never fitted on one: nine players on a phone was a
// two-thousand-pixel column watched by scrolling. One player per slide fitted,
// and paid for it by never showing the round as a round — every answer was
// gone by the time the next one arrived, so nobody could see that four people
// had the same seven and one had an eight, which is the whole conversation the
// reveal exists to start.
//
// So: neither. The letters reveal is a single panel that fills the stage, and
// answers land in it one at a time, shortest declared first, each **inserted at
// the top** with the rows below sliding down. Because the queue ascends, the
// best answer of the round finishes on top, which makes the panel a leaderboard
// that builds itself. The row that just landed is spotlit, and the host's
// overrule is on the row rather than on a slide you have to navigate back to.
// The old stack's virtue — nothing already read is taken away — comes back, and
// the count stays fixed because rows shrink with the number of them.
//
// Nothing above is true of numbers. Six sets of working do not go nine up, so a
// numbers round keeps a slide per player. The conundrum went the other way
// entirely: see `buildSlides`.
//
// Five things here are load-bearing rather than decorative:
//
//   1. The server paces it. `round.revealCursor` says how many entries of
//      `round.revealOrder` are out, and the stage shows the entry at the cursor.
//      It used to accumulate the sequence from socket messages and play it back
//      locally, which meant every client ran its own clock and nobody was
//      looking at the same answer at the same moment. Nothing here counts
//      anything down.
//   2. Stepping back is local and temporary. Anyone may look at an answer that
//      has gone past, and the next thing the server releases pulls them back to
//      it. So the room is together whenever the room is moving, and the review
//      only exists in the gaps the server leaves.
//   3. The end of the reveal is a pause, not a gap. The host decides when to
//      move on, and the auto-advance is shown honestly against the server's
//      clock rather than the device's. One press moves the game on, and it is
//      the only one: this screen's closing slide *is* the standing between two
//      rounds, so the press ends the round rather than showing anything. The
//      chat that goes with the pause is the room shell's, beside this screen
//      rather than inside it.
//   3a. The host can also stop the clock, at any point in the reveal, and that
//      is the one other control here. It changes nothing about what happens
//      next: it holds the wait for the next answer, or the wait for the next
//      round, because both are one clock and the room talks over both.
//   3b. The payoff slides arrive one beat apart, and the stagger is what makes
//      them visible at all. Following the server means following it to the
//      *newest* slide, so two slides appearing in the same frame means the
//      earlier one is never on the stage. That is not hypothetical: the last
//      answer and `revealComplete` land in the same payload, so before the
//      beats existed the reveal jumped straight over the final word — which,
//      the queue being shortest-first, is the best word in the round.
//   4. Team scoring is best-of, not a sum, so the member whose answer carried
//      the round is named. Without that, a team total that is not the sum of its
//      rows reads as a bug to everyone who can add up.
//   5. Nothing appears over an answer. The rejection stamp that used to sit
//      across the tiles is not coming back; see the note above `RulingBar`.
//
// **A panel row is two lines and one score**, and the shape it replaced was
// reported from a phone. It was one line — name, declared length, word, reason,
// score — with the name in a `7ch` box and the declared length in a `3ch` one.
// Three things were wrong with it and they were the same thing: a row that is
// one line has to buy every column out of the same 326 pixels. Names came out
// as four characters and an ellipsis. `Not in the dictionary` and `Disallowed`
// ran out of the row. And the declared length, a bare number on the left of a
// row that ends in another bare number, read as the score twice — which is what
// it was reported as.
//
// So the name and its badge take the top line with all the room they need, the
// word takes the second in the tile face, and the score is said once, on the
// right, centred against both. A ruled-out word is the word itself struck
// through plus a short chip — `not a word`, `not on the board`, `host ruled` —
// never a sentence about it. The declared length is only drawn when it disagrees
// with what was played, as `claimed n`, because that is the only time it is a
// second fact rather than the word's own length counted twice.
//
// **A row says who, what they played, and what it scored, and nothing else.**
// The handle, the `guest` tag and the `signed in` tag are off the whole stage —
// see `showIdentity` — and the red outline a ruled-out row used to wear is
// gone with them. Both were reported as clutter and both were the screen saying
// something it had already said, or something that is not about the round.
//
// **The strikethrough is not the stamp coming back.** The stamp was a rotated
// plate positioned over the tiles, which hid the word at the one moment its
// owner most wanted to read it. A strikethrough is drawn by the text run that
// draws the word: it cannot cover a neighbour, it moves with the glyphs, and the
// word underneath it keeps `ink` — full contrast, not the dimmed alert red it
// used to be given. Nothing on this screen may be positioned over an answer, and
// nothing here is.
// ---------------------------------------------------------------------------
//
// **The screen is this file; the reveal is the directory beside it.** It passed
// two and a half thousand lines and was split by the seam it already had: every
// frame takes one prop, `view: RevealView`, so each group of frames could leave
// without learning anything new. What stayed is what owns the sequence — the
// stage, the cursor, the two components that move it, and the control bar.
//
//   `reveal/ruling.ts`             how a judged answer reads, and how a ruling is sent
//   `reveal/view.ts`               `RevealView`, `Slide`, and what slides a round has
//   `reveal/atoms.tsx`             the small things all of them use
//   `reveal/RunningOrder.tsx`      the board strip and the rail above the stage
//   `reveal/frames.tsx`            one answer, one frame, and the closing frames
//   `reveal/LettersLeaderboard.tsx` the letters panel that builds itself
//   `reveal/aggregate.ts`          what a room too big to list says, and when
//   `reveal/AggregateBoard.tsx`    the answers with the room gathered behind them
//   `reveal/RoundDistribution.tsx` the two histograms over the whole room
//   `reveal/RoundScores.tsx`       the standing
//
// `RevealFooter` did not go with them, and the reason is on `FOOTER_HEIGHT`.
// ---------------------------------------------------------------------------
//
// **What a word means, on the screen the room is watching.** Every word the
// reveal puts up is a control that asks for its definition — the answers as
// they are released, a conundrum's solution and the AI's shadow answers, the
// best word and its alternatives on the payoff. The ways in are the record
// page's, unchanged, and `reveal/atoms.tsx` says which words those are and why.
//
// **The answer floats beside its word, and this reverses the slot that shipped
// with it.** It used to be a line between the stage and the control bar: a
// `shrink-0` sibling of a `flex-1 min-h-0` stage, so the stage paid for its
// height and the column's total never changed. That was true and it was not
// enough. The stage RESIZING is itself the shift — the answers on it move and
// the frame reflows — and it happened on every pointer crossing every word.
// Isaac reported it as massive layout shift on hover, which is what it was.
//
// So the answer is a `position: fixed` panel portalled to `document.body`,
// placed against the word's own rect. `WordDefinition.tsx` owns the placement
// and the reasoning; three things about it are this screen's:
//
//   - **The invariant still holds, and portalling is what holds it.** STATUS
//     item 11: this screen is exactly the viewport with the overflow off,
//     `scrollHeight === clientHeight` in every state at 390x844. A panel inside
//     this column would be CLIPPED by that same `overflow-hidden`; at body level
//     it is outside every ancestor's overflow, and a fixed element adds nothing
//     to any scroll height. Nothing in the column moves when one opens — there
//     is no slot to render and no sibling to insert.
//   - **It still does not have to be dismissed**, which was the real objection
//     to a bubble here. A panel over the control bar, the chevrons or the
//     running order would take away the three things a viewer needs while it is
//     open — so it is `pointer-events-none` and presses go straight through it.
//     Nothing here takes a keystroke either, so the arrows still step the reveal
//     with a word focused.
//   - **It is outside `SlideSwap`** as the slot was, which remounts on every
//     slide: a panel inside it would slide in from the side and be animated away
//     by moving the stage. A portal is outside everything by construction.
//
// Nothing is drawn while the request is in flight, and something is always drawn
// once it lands — including for the two words in three with no gloss, which now
// say so rather than reading as a press that did nothing. See `WordDefinition.tsx`
// for the half of that rule that did not change: no definition is ever invented.
// ---------------------------------------------------------------------------

import { useEffect, useRef, useState } from 'react';
import { effectiveDeadline } from '@countdown/shared';
import type { BestAnswer, ClientRound, PublicPlayer } from '@countdown/shared';
import { Button, IconButton } from '../../components/primitives/Button.js';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  Icon,
  PauseIcon,
  PlayIcon,
} from '../../components/primitives/Icon.js';
import { Frame } from '../../components/round/atoms.js';
import { DefinitionPopover } from '../../components/round/WordDefinition.js';
import { RoundTypeChip } from '../../components/primitives/RoundTypeChip.js';
import { useKeyboardMode } from '../../hooks/useKeyboardMode.js';
import { useIsDesktop } from '../../hooks/useMediaQuery.js';
import { useVisualViewport } from '../../hooks/useVisualViewport.js';
import { useWordDefinition } from '../../hooks/useWordDefinition.js';
import { playerById, seatedPlayers } from '../../lib/playerLookup.js';
import {
  releasedIds,
  revealDisplayOrder,
  revealHasFinished,
  revealPayoffMs,
} from '../../lib/revealSequence.js';
import { STAGE_FILL, SlideSwap, useSecondsUntil } from './reveal/atoms.js';
import {
  AnswerFrame,
  BestWordFrame,
  ConundrumSolutionFrame,
  NothingFrame,
  NumbersAnswerFrame,
  ShadowAnswersFrame,
  SolverFrame,
} from './reveal/frames.js';
import { AggregateBoard } from './reveal/AggregateBoard.js';
import { LettersLeaderboard } from './reveal/LettersLeaderboard.js';
import { RoundDistribution } from './reveal/RoundDistribution.js';
import { RoundScores } from './reveal/RoundScores.js';
import { BoardStrip, RunningOrder } from './reveal/RunningOrder.js';
import { answerTextOf, overrideMessage, undoRulingMessage } from './reveal/ruling.js';
import { buildSlides, payoffOrder } from './reveal/view.js';
import type { RevealView, Slide } from './reveal/view.js';
import type { RoomScreenProps } from './types.js';

// --- The stage -------------------------------------------------------------

/**
 * Nothing is out yet, which is a real state rather than an empty one: the
 * declared lengths are already public on the rail above and the board is on the
 * strip above that.
 *
 * It carried a sentence saying the answers come out shortest first, and a
 * conundrum's said the answer was coming. The lead is 900ms — nobody reads a
 * sentence in it — and the conundrum does not wait at all now.
 */
function WaitingFrame() {
  return (
    <Frame className={`${STAGE_FILL} items-center justify-center text-center`}>
      <span className="label-mono">the reveal</span>
    </Frame>
  );
}

function Stage({ view, slide }: { view: RevealView; slide: Slide | undefined }) {
  if (slide === undefined) return <WaitingFrame />;

  if (slide.kind === 'scores') {
    return (
      <RoundScores
        state={view.state}
        round={view.round}
        title={`Round ${view.round.index + 1} scores`}
      />
    );
  }

  if (slide.kind === 'leaderboard') return <LettersLeaderboard view={view} />;

  // The two slides a large room gets instead of a queue: the answers with the
  // people who found them gathered behind each one, and then the whole room as
  // two histograms. `reveal/aggregate.ts` says when each is allowed on the stage
  // and why the pacing is a table plus a finale rather than a longer queue.
  if (slide.kind === 'aggregate') return <AggregateBoard view={view} />;

  if (slide.kind === 'distribution') return <RoundDistribution view={view} />;

  if (slide.kind === 'shadows') return <ShadowAnswersFrame view={view} />;

  if (slide.kind === 'best') {
    const best = view.best;
    if (best === null) return <WaitingFrame />;
    if (best.kind === 'letters') return <BestWordFrame view={view} best={best} />;
    if (best.kind === 'numbers') return <SolverFrame view={view} best={best} />;
    return <ConundrumSolutionFrame view={view} best={best} />;
  }

  // Not a cast anywhere below. A released player with no submission is a player
  // who sent nothing, which is a thing to draw rather than a thing to assume
  // away.
  const submission = view.submissionOf(slide.player.id);

  if (view.round.type === 'numbers') {
    const steps = submission?.steps ?? [];
    if (submission === undefined || (submission.value ?? null) === null || steps.length === 0) {
      return <NothingFrame view={view} player={slide.player} />;
    }
    return <NumbersAnswerFrame view={view} player={slide.player} submission={submission} />;
  }

  if (submission === undefined || answerTextOf(submission).length === 0) {
    return <NothingFrame view={view} player={slide.player} />;
  }
  return <AnswerFrame view={view} player={slide.player} submission={submission} />;
}

// --- The bar at the foot ---------------------------------------------------

/**
 * The bar's height, and therefore the space kept for it in the flow.
 *
 * One number in two places on purpose, and they are three lines apart rather
 * than in two files: the bar is out of the flow, so a spacer that disagreed with
 * it would either cover the last row of the panel or leave a strip of nothing
 * under it. `RevealFooter` draws itself at exactly this height in every state.
 *
 * **This is why `RevealFooter` stayed in this file when the rest of the reveal
 * went out to `reveal/`.** Every other group left cleanly — the frames take one
 * prop and nothing else — but the bar and the spacer are one measurement, and
 * the only two ways to split them were to export the constant into a second
 * file, or to leave the pair where they are. The comment above says which of
 * those is the bug, so the pair stayed. If the footer ever does move, the
 * constant moves with it and the spacer imports it, not the other way round:
 * the spacer is three lines of markup and the bar is the thing that has to be
 * exactly this tall.
 */
const FOOTER_HEIGHT = 78;

/**
 * One bar, one height, and every state of the reveal sits in it. It is in the
 * layout from the first paint, so nothing shoves when the last answer lands, and
 * it is pinned to the bottom of a screen that does not scroll, so it is
 * reachable from every slide without going looking for it.
 *
 * **Two controls, and only the second one changes what happens next.** The
 * advance is named after where the game goes rather than after a screen: this
 * reveal's closing slide is the standing between two rounds, so the press is
 * what ends the round. The hold stops the room on the standing and decides
 * nothing.
 *
 * **The hold has two shapes and they are one press.** Pressed while answers are
 * still coming out it ARMS: the queue runs to the end, the payoff plays, and the
 * room stops on the standing. Pressed once everything is out it stops the
 * auto-advance where it is, which is the same place. It used to freeze the
 * entry clock instead, so a host who wanted the room to be able to talk had to
 * cut the reveal off — and the queue is worst-first, so what got cut off was the
 * round's best answer. See `setRevealPaused` in the engine.
 *
 * The countdown is the subject of that button, which is why it is beside it and
 * why it runs in both waits rather than only at the end. Stopped, it says so
 * instead of counting: the deadline it would read from moves when the hold is
 * lifted, so a number ticking down under a held clock would be a lie with a
 * decimal point on it. ARMED it keeps counting, because the clock it is counting
 * is genuinely still running — the hold has not happened yet.
 *
 * The minimum height is the tallest state — a 52px button and its padding — and
 * not the shortest. Reserved from the first paint, the bar does not grow when
 * the last answer lands and take a slice off the stage as it goes.
 *
 * **It is pinned, and it is exactly `FOOTER_HEIGHT` tall in every state.** The
 * complaint was that it walked off the bottom of a phone as the panel grew, so
 * it is now taken out of the flow and held against the bottom of the screen,
 * with a spacer of the same height standing in its place so it can never come
 * down on the last row. `coveredHeight` rather than a bare `bottom: 0`, because
 * iOS does not resize the layout viewport when a keyboard arrives — it scrolls
 * the visual one, and a bar pinned at zero ends up behind the keys. Nothing on
 * this screen takes a keystroke, but the room's chat drawer does, and the hook
 * is the one place that knows.
 *
 * **`coveredHeight` and not `bottomInset`, and the difference is a bug Isaac
 * saw.** `bottomInset` is `innerHeight - height - offsetTop`, and both of the
 * subtracted terms move on an ordinary scroll while nothing is covering any more
 * of the screen: iOS already carries a `fixed` element with `offsetTop`, so
 * subtracting it corrects the same slide twice, and `innerHeight` is not the box
 * a fixed element is laid out in — that is the initial containing block, and
 * `innerHeight` grows and shrinks as Safari's toolbars collapse. The bar rode up
 * and down the page. `coveredHeight` is how much is covered and nothing else, so
 * a scroll cannot move it.
 *
 * The height being exact is what makes the spacer honest, so **the row does not
 * wrap.** It used to, and a wrapped bar is 120px rather than 78 — which is a
 * spacer that is wrong by a whole row of the panel. The left-hand group shrinks
 * to nothing instead: the progress track goes first, then the count, and the
 * words in front of the number were already a phone's to give up.
 */
function RevealFooter({
  view,
  reviewing,
  coveredHeight,
  onAdvance,
  onHold,
}: {
  view: RevealView;
  /** Position in the sequence while looking back at something already shown. */
  reviewing: { at: number; of: number } | null;
  /** How much of the screen something is covering. See the note above. */
  coveredHeight: number;
  onAdvance: () => void;
  onHold: (paused: boolean) => void;
}) {
  const { state, round, ordered, released, finished } = view;
  const total = ordered.length;

  const [advancing, setAdvancing] = useState(false);
  // A press that the server never answers must not leave a dead button behind.
  useEffect(() => {
    if (!advancing) return;
    const timer = setTimeout(() => setAdvancing(false), 4_000);
    return () => clearTimeout(timer);
  }, [advancing]);

  const clock = round.clock;
  // The host's own hold, told apart from a server pause: a server pause has its
  // own overlay over this screen and is nothing the host may lift from here.
  const stopped = clock !== null && clock.pausedAt !== null && !state.serverPaused;
  // Armed, and still running. A hold pressed while answers are coming out does
  // not stop them — it stops the standing when they run out — so the clock is
  // genuinely still going and the countdown beside it is genuinely still true.
  // See `setRevealPaused` in the engine for why that is the behaviour.
  const armed = round.revealHoldAtEnd && !stopped && !state.serverPaused;
  /** The hold is on, whichever of its two shapes it is currently in. */
  const holding = stopped || armed;

  // One clock, both waits. `revealAdvanceAt` is the server's readable copy of
  // the deadline once every entry is out; before that the same deadline is the
  // next entry's, and it lives nowhere but on the clock.
  const seconds = useSecondsUntil(
    stopped
      ? null
      : finished
        ? round.revealAdvanceAt ?? null
        : clock === null
          ? null
          : effectiveDeadline(clock),
  );
  const hostName = playerById(state, state.hostId)?.displayName ?? 'the host';

  // Read off the sequence rather than off `totalRounds`, because that is the
  // list the server walks: on the last round the press ends the game, and a
  // button promising a next round that does not exist is the kind of lie a host
  // only finds out about by pressing it.
  const lastRound = state.settings.roundSequence[state.currentRoundIndex + 1] === undefined;

  // The words in front of the number are what a phone gives up, because they
  // are the part the label beside them has already said. Measured: at 390 the
  // bar has 326px inside its padding, and the full line, the hold and the
  // advance want 369 — a wrapped button and 43px off the stage at the exact
  // moment the round pays off.
  const timer = stopped ? (
    <span className="label-mono">held</span>
  ) : seconds === null ? null : (
    <span className="font-mono text-xs text-ink-dim">
      {/* Armed says where it will stop, not that it has. The reveal is still
          running and the number beside this is still counting down to the next
          answer, so anything reading as "paused" here would be the bar
          contradicting the screen above it. */}
      <span className="hidden sm:inline">
        {armed ? 'holding at the standing · ' : finished ? 'moving on in ' : 'next in '}
      </span>
      <span className="tabular inline-block min-w-[2.5ch] text-right">{seconds}</span>s
    </span>
  );

  return (
    // Out of the flow and against the bottom of the pane, which on a phone is
    // the bottom of the screen. `data-reveal-footer` is how the test finds it,
    // because the pin is the point rather than the class list that draws it.
    <div
      data-reveal-footer=""
      // The height is inline rather than a utility so it and the spacer are one
      // constant. A `h-[78px]` here would be a second copy of the number that
      // Tailwind, not TypeScript, gets to decide has drifted.
      style={{ bottom: coveredHeight, height: FOOTER_HEIGHT }}
      className="absolute inset-x-0 z-10 flex items-center gap-x-3 rounded-frame border border-line bg-bg px-4 sm:gap-x-4 sm:px-5"
    >
      {/* Everything that gives way, in the order it gives way. */}
      <div className="flex min-w-0 flex-1 items-center gap-3 overflow-hidden">
        {finished ? (
          state.youAreHost ? null : (
            <p className="min-w-0 truncate text-[13px] text-ink-muted sm:text-sm">
              waiting for {hostName}
            </p>
          )
        ) : // A conundrum has one answer and it is already on the stage, so there
        // is no queue to be part of the way through. The count is over the
        // seated players, which on that round is not what is being revealed.
        round.type === 'conundrum' ? null : (
          <>
            <span className="label-mono shrink-0">
              {reviewing === null ? 'revealing' : 'looking back'}
            </span>
            {/* One text node, and tabular across the whole line: the count
                changing must not re-lay the sentence out around it. */}
            <span className="tabular shrink-0 font-mono text-xs text-ink-dim">
              {reviewing === null
                ? `${released.length} of ${total}`
                : `${reviewing.at} of ${reviewing.of}`}
            </span>
            <div
              aria-hidden="true"
              className="h-1 min-w-4 flex-1 overflow-hidden rounded-full bg-surface-2"
            >
              <div
                className="h-full rounded-full bg-blue-500 transition-[width] duration-700 ease-out"
                style={{ width: `${total === 0 ? 0 : (released.length / total) * 100}%` }}
              />
            </div>
          </>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-3">
        {timer}
        {state.youAreHost ? (
          // **A transport pair, and it used to be the word `Hold`.** Isaac read
          // that as an instruction to press and keep pressing — which is what
          // the word means everywhere else in this app, on `HoldButton`, where
          // holding a button down is exactly the interaction. A pause mark and
          // a play mark say what the next press does and cannot be read as a
          // gesture.
          //
          // The label is what carries the difference between the hold's two
          // shapes, because the mark cannot: armed and stopped are both "the
          // hold is on", and pressing either lifts it.
          <IconButton
            size="sm"
            variant="outline"
            label={
              holding
                ? 'Let the reveal carry on'
                : finished
                  ? 'Hold the reveal'
                  : 'Hold the reveal at the standing'
            }
            aria-pressed={holding}
            // Nothing to hold before the server has put the reveal on a clock,
            // and a server pause is not the host's to lift from here.
            disabled={clock === null || state.serverPaused}
            onClick={() => onHold(!holding)}
          >
            <Icon icon={holding ? PlayIcon : PauseIcon} size={18} />
          </IconButton>
        ) : null}
        {finished && state.youAreHost ? (
          <Button
            size="lg"
            loading={advancing}
            onClick={() => {
              setAdvancing(true);
              onAdvance();
            }}
          >
            {lastRound ? 'Final scores' : 'Next round'}
          </Button>
        ) : null}
      </div>
    </div>
  );
}

// --- Screen ----------------------------------------------------------------
//
// The chat used to be here: a column on a desktop, a dock that took a share of
// the same column on a phone. It belongs to the room shell now, so this screen
// renders its own content into its own container and the shell puts the chat
// beside it. One chat for the whole room, rather than one per screen that has
// to be kept from dropping somebody mid-sentence on the way between two of
// them.

export function RevealScreen({ state, send }: RoomScreenProps) {
  const round: ClientRound | undefined = state.rounds[state.currentRoundIndex];
  const keyboard = useKeyboardMode();
  const isDesktop = useIsDesktop();
  // Only for the pinned bar. Inside `/dev`'s iframe the hook declines to report
  // an inset at all, which is why the gallery does not read a keyboard that is
  // not there — see the note in the hook.
  const viewport = useVisualViewport();

  const players = seatedPlayers(state);
  const finished = round === undefined ? false : revealHasFinished(round);
  const roundIndex = round?.index ?? -1;

  // How far into the payoff the beat has got. It acts on data that has already
  // arrived — every closing slide is in the payload the moment the reveal
  // completes — so nothing a player can see is gated on a local countdown that
  // could disagree with the server. What it decides is only the order things
  // reach the stage in.
  //
  // It records which round it counted for rather than a bare number, so a
  // screen that survives into the next round does not start it already spent.
  const [payoff, setPayoff] = useState<{ round: number; beats: number }>({
    round: -1,
    beats: 0,
  });
  const beats = payoff.round === roundIndex ? payoff.beats : 0;

  // Which slide is on the stage, and which way the last move went.
  const [cursor, setCursor] = useState(0);
  const [direction, setDirection] = useState<1 | -1>(1);

  /**
   * The word the screen is asking about, the round it was asked in, and the
   * button it was asked from.
   *
   * The round is carried for the same reason the payoff carries it: a screen
   * that survives into the next round must not open it holding the last one's
   * word, and round two's leaderboard is the same slide key as round one's. The
   * SLIDE is handled the other way, by `ClearPickOnSlide` — a word that has left
   * the stage cannot be checked against the panel naming it, and a panel
   * pointing at a button that is no longer there points at nothing.
   *
   * `anchor` is the DOM node, held deliberately: the answer is placed against
   * the word's rect, and the rect is the one thing no amount of React state can
   * reconstruct. It is only ever read while the pick is current, and the popover
   * checks `isConnected` before it reads one at all.
   */
  const [picked, setPicked] = useState<{
    round: number;
    word: string;
    anchor: HTMLElement;
  } | null>(null);
  const current = picked !== null && picked.round === roundIndex ? picked : null;
  const asking = current?.word ?? null;

  /**
   * `liveGameId`, and this is the whole of why the reveal could not be wired
   * when definitions shipped on the record page: `gameId` is the id of the
   * WRITTEN RECORD and is null for the whole of a live game, so there was no id
   * to ask with. `liveGameId` is the room's own, set from the moment the game
   * starts. `round.index` and not the position in `state.rounds`, because the
   * round's own index is what the endpoint takes.
   *
   * Nothing is asked before `revealedAt`. The endpoint would refuse it — its
   * membership set is empty until the round is judged — and the client must not
   * be the thing that discovers that.
   */
  const definitionGameId =
    round === undefined || round.revealedAt === null ? null : state.liveGameId;
  const definition = useWordDefinition(
    asking === null || definitionGameId === null || round === undefined
      ? null
      : { gameId: definitionGameId, roundIndex: round.index, word: asking },
  );

  if (round === undefined) {
    return <p className="p-6 text-ink-dim">The round is being scored.</p>;
  }

  const order = revealDisplayOrder(round, players);
  const byId = new Map(players.map((player) => [player.id, player]));
  const ordered = order
    .map((playerId) => byId.get(playerId))
    .filter((player): player is PublicPlayer => player !== undefined);

  const released = releasedIds(round, players);
  const releasedSet = new Set(released);

  const best: BestAnswer | null = finished ? round.best : null;

  const view: RevealView = {
    state,
    round,
    ordered,
    released,
    isReleased: (playerId) => releasedSet.has(playerId),
    // Gated on the cursor everywhere it is read, so an answer that arrives in
    // the payload ahead of its turn still cannot be rendered ahead of its turn.
    //
    // The conundrum's solver is the one exception, and it is the round's shape
    // rather than a hole in the gate. That round has exactly one answer, the
    // whole room is already looking at the nine letters it came out of, and the
    // cursor exists to pace a queue. Nothing is leaked by it either: the payload
    // this reads carries no answers at all until the round is judged.
    submissionOf: (playerId) =>
      releasedSet.has(playerId) ||
      (round.type === 'conundrum' && playerId === (round.solvedBy ?? null))
        ? round.submissions[playerId]
        : undefined,
    declaredOf: (playerId) => round.submissions[playerId]?.declaredLength ?? null,
    finished,
    best,
    beats,
    tileSize: isDesktop ? 'board' : 'phone',
    onOverride: (playerId, allow) => send(overrideMessage(playerId, allow)),
    onUndoRuling: (playerId) => send(undoRulingMessage(playerId)),
    definitionGameId,
    pickedWord: asking,
    onPickWord: (word, anchor) => setPicked({ round: roundIndex, word, anchor }),
  };

  const slides = buildSlides(view);
  const latest = slides.length - 1;
  const index = Math.min(Math.max(cursor, 0), Math.max(latest, 0));
  const slide = slides[index];
  const reviewing = index < latest ? { at: index + 1, of: slides.length } : null;
  const payoffTotal = payoffOrder(view).length;

  /**
   * A closing slide the beat has not let out yet, which a press may let out.
   *
   * The payoff slides are all in the payload the moment the reveal completes —
   * `PayoffBeats` only spaces them — so hurrying one along shows nothing that
   * was not already this client's to see. That is what makes this local and
   * safe, and it is exactly not true of the entry queue: `latest` is still a
   * hard stop while the server is pacing answers out, because the cursor is the
   * contract and one player running ahead of the room would break it.
   */
  const beatToRelease = finished && beats < payoffTotal;

  /**
   * Move the stage. Refuses to go past what the server has released — except
   * for a payoff beat, which is this client's own timer and which a press may
   * therefore skip.
   *
   * Isaac, on being made to sit through the board's best word before the
   * standing: he wanted to be able to go one page right under his own agency.
   * Releasing the beat is enough on its own — `FollowServer` follows `latest`,
   * so the new slide takes the stage the moment it exists, and the cursor does
   * not have to be moved here as well.
   */
  const step = (to: number) => {
    if (to > latest) {
      if (!beatToRelease) return;
      setDirection(1);
      setPayoff({ round: roundIndex, beats: beats + 1 });
      return;
    }
    if (to < 0) return;
    setDirection(to >= index ? 1 : -1);
    setCursor(to);
  };

  return (
    // A hard cut into the phase, and one fade on mount so arriving here is a
    // beat rather than a jolt. The class is constant, so it does not re-run as
    // the cursor moves.
    //
    // **`h-full` is what actually holds this screen to the pane, and `flex-1` on
    // its own never did.** The intent was always that the reveal is exactly the
    // pane the room shell gives it, with anything that cannot fit scrolling
    // inside the stage rather than dragging the controls off the bottom. But the
    // shell does not hand a screen straight to a flex container: `PhaseCrossfade`
    // wraps it in a plain block so it can lay the outgoing screen over the
    // incoming one, and `flex-1` and `min-h-0` mean nothing to a block parent.
    // So the column grew with the panel — measured at 921px inside a 755px pane
    // with eight answers out — and the control bar went down with it, which is
    // the complaint. `h-full` resolves against that block, which is itself
    // `h-full` of a flex item with a definite height, so the chain reaches the
    // viewport. In `/dev`, where a screen sits in a box on a long page, the
    // parent has no definite height, `h-full` computes to `auto`, and the screen
    // is its own content — which is what that page wants.
    //
    // It was `h-dvh` when the screen owned the whole page, which inside the
    // shell would be the viewport *plus* the header.
    <div className="fade-in flex h-full min-h-0 flex-1 flex-col overflow-hidden">
      <FollowServer latest={latest} onFollow={setCursor} onDirection={setDirection} />
      {/* Stopped while somebody is looking back, so the payoff never pulls a
          viewer off an answer they stepped back to. The server's own releases
          still pull, because that rule is not local; this one is, and a beat
          that yanked the host out of a ruling would be the screen arguing with
          the person using it. */}
      <PayoffBeats
        active={finished && reviewing === null}
        beats={beats}
        of={payoffTotal}
        pacePercent={state.settings.revealPacePercent}
        onBeat={(next) => setPayoff({ round: roundIndex, beats: next })}
      />
      <KeyboardStepping
        enabled={keyboard.mode === 'answer'}
        onStep={(delta) => step(index + delta)}
      />
      <ClearPickOnSlide slideKey={slide?.key ?? 'waiting'} onClear={setPicked} />

      {/* One column, and it stops at 860. The two-column version of this was
          the screen and its own chat; the chat is the shell's now, so a wider
          box here would only stretch the stage away from the eye. */}
      <div className="mx-auto flex min-h-0 w-full max-w-[720px] flex-1 flex-col gap-2.5 px-4 py-3 sm:max-w-[860px] sm:gap-3.5 lg:py-5">
        {/* `relative` is what the control bar is pinned against. It is this
            column rather than the page, so the bar spans the stage and not the
            chat beside it on a desktop — and in `/dev`, where a screen is a box
            on a long page rather than the whole viewport, it stays inside its
            own box instead of a dozen of them stacking on the window. The
            column is `flex-1` inside a pane the room shell holds at viewport
            height, so its bottom edge IS the bottom of the screen. */}
        <div className="relative flex min-h-0 min-w-0 flex-1 flex-col gap-2.5 sm:gap-3.5">
          <header className="flex shrink-0 flex-wrap items-center gap-3">
            <RoundTypeChip type={round.type} size="sm" />
            <h1 className="text-lg font-bold sm:text-xl">Reveal</h1>
            <span className="label-mono ml-auto">
              round {round.index + 1} of {state.totalRounds}
            </span>
          </header>

          {/* The board stays up for the whole reveal, the scores included. It
              used to stand down for the scores on the grounds that nine rows
              need every pixel, and what that actually did was drop the numbers
              round's target out of the layout at the exact moment the scores
              arrived, so the rail, the stage and the control bar all jumped up
              the screen. The row is 52px and the scores fit without them; a
              target you cannot see while reading "745, within 5" is worth less
              than the row costs. */}
          <BoardStrip view={view} />

          {/* The rail is the declared list and the way back, and a conundrum has
              neither: it declares nothing, and it is one slide plus the scores.
              The chevrons stay either way, so the scores are never a one-way
              door, and the empty flex keeps the row the same height it is on
              every other round. */}
          <div className="flex shrink-0 items-center gap-1.5">
            <IconButton
              size="sm"
              variant="outline"
              label="Previous answer"
              disabled={index <= 0}
              onClick={() => step(index - 1)}
            >
              <Icon icon={ChevronLeftIcon} size={18} />
            </IconButton>
            <div className="min-w-0 flex-1">
              {round.type === 'conundrum' ? null : (
                <RunningOrder view={view} slides={slides} index={index} onSelect={step} />
              )}
            </div>
            <IconButton
              size="sm"
              variant="outline"
              label="Next answer"
              disabled={index >= latest && !beatToRelease}
              onClick={() => step(index + 1)}
            >
              <Icon icon={ChevronRightIcon} size={18} />
            </IconButton>
          </div>

          {/* The handle is suppressed by a prop on every chip now, the
              standing's included — `ScoreRow` forwards `showIdentity` to the
              chips this screen does not render itself. */}
          <div data-reveal-stage="" className="min-h-0 flex-1">
            <SlideSwap key={slide?.key ?? 'waiting'} direction={direction}>
              <Stage view={view} slide={slide} />
            </SlideSwap>
          </div>

          {/* The bar's place in the flow, held open while the bar itself sits
              over it. Without this the last row of the panel would be under the
              bar rather than above it, which is the other half of pinning
              anything. It carries `coveredHeight` too — the same number the bar
              is pinned by, so the pair stays exact — or a keyboard would lift
              the bar onto a row the stage still believes it can use. */}
          <div
            aria-hidden="true"
            data-footer-reserve=""
            className="shrink-0"
            style={{ height: FOOTER_HEIGHT + viewport.coveredHeight }}
          />

          {/* The host's two presses. Typed against the protocol rather than cast
              past it: if either message is not on ClientMessage, that is a type
              error worth having. The third, the undo, is the exception and it is
              argued out at `undoRulingMessage`. */}
          <RevealFooter
            view={view}
            reviewing={reviewing}
            coveredHeight={viewport.coveredHeight}
            onAdvance={() => send({ type: 'advance-reveal' })}
            onHold={(paused) => send({ type: 'set-reveal-paused', paused })}
          />

          {/* What the picked word means. **It draws nothing at all in this
              tree** — it is a portal to `document.body` — so it is written last
              rather than where it appears, which is beside the word it was
              asked about. That is what keeps the column exactly the height it
              was before anybody pressed anything. See the header. */}
          {current === null ? null : (
            <DefinitionPopover word={current.word} anchor={current.anchor} answer={definition} />
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Follows the server.
 *
 * A viewer who has stepped back is pulled to whatever the server released next,
 * so the room is looking at the same answer whenever the reveal is moving. The
 * looking-back only lives in the gaps, which is the honest reading of "the
 * server paces it": the pacing is not a suggestion that a local cursor may
 * ignore.
 *
 * Its own component so the effect depends on one number and nothing else. Inside
 * the screen it would sit downstream of every render the cursor causes.
 */
function FollowServer({
  latest,
  onFollow,
  onDirection,
}: {
  latest: number;
  onFollow: (next: number) => void;
  onDirection: (direction: 1 | -1) => void;
}) {
  useEffect(() => {
    onDirection(1);
    onFollow(Math.max(latest, 0));
  }, [latest, onFollow, onDirection]);
  return null;
}

/**
 * Drops the picked word when the stage moves on.
 *
 * The line under the stage names the word it is about, so it is never
 * misleading — but a word that has left the screen is a line you cannot check
 * against anything, and the space it holds is space the slide that just arrived
 * wants. The letters leaderboard is one slide for the whole round, so a word
 * picked there survives every release the server makes; the payoff is what takes
 * it away, and by then the word is gone from the stage.
 *
 * Its own component so the effect depends on one string and nothing else, the
 * same reason `FollowServer` is one. `onClear` is the state setter itself rather
 * than an inline arrow, so the effect does not re-run on every render the cursor
 * causes.
 */
function ClearPickOnSlide({
  slideKey,
  onClear,
}: {
  slideKey: string;
  onClear: (picked: null) => void;
}) {
  useEffect(() => {
    onClear(null);
  }, [slideKey, onClear]);
  return null;
}

/**
 * Lets the closing slides onto the stage one at a time.
 *
 * All of them are in the payload the moment the reveal completes, so this is
 * spacing rather than pacing. It has to exist because `FollowServer` follows to
 * the newest slide: anything that appears in the same frame as something after
 * it is stepped over before it is ever drawn.
 *
 * **The first beat and the rest are different lengths, and one number for both
 * was the complaint.** The first is a seam: the stage is holding the last answer
 * and the engine has already given that answer a full six-second entry of its
 * own, so a long wait here is a second hold on a thing already read. Every beat
 * after it is a slide's entire screen time — for a letters round that is the
 * board's best word, the punchline of the round, and it was getting the same
 * beat as a page transition.
 *
 * Its own component so the timer depends on the beat count and nothing else.
 * Inside the screen it would sit downstream of every render the cursor causes,
 * and the reveal re-renders on every cursor move.
 */
function PayoffBeats({
  active,
  beats,
  of,
  pacePercent,
  onBeat,
}: {
  active: boolean;
  beats: number;
  of: number;
  pacePercent: number;
  onBeat: (next: number) => void;
}) {
  // Reached through a ref for the same reason `KeyboardStepping` does it: an
  // inline callback is a new function every render, and a timer keyed on it
  // would restart before it ever fired.
  const beatRef = useRef(onBeat);
  useEffect(() => {
    beatRef.current = onBeat;
  });

  useEffect(() => {
    if (!active || beats >= of) return;
    // Scaled by the room's own pace, like every other beat in the reveal. A
    // payoff on a fixed clock is the one slide the pace control cannot reach.
    const payoff = revealPayoffMs(pacePercent);
    const wait = beats === 0 ? payoff.leadMs : payoff.beatMs;
    const timer = setTimeout(() => beatRef.current(beats + 1), wait);
    return () => clearTimeout(timer);
  }, [active, beats, of, pacePercent]);

  return null;
}

/** Left and right step through the reveal, but only while chat has not taken the
 *  keyboard. Every other screen gives the keys to the answer; here there is no
 *  answer in flight, so they belong to the sequence. */
function KeyboardStepping({
  enabled,
  onStep,
}: {
  enabled: boolean;
  onStep: (delta: 1 | -1) => void;
}) {
  // The listener is bound once per enabled state, so it reaches the current
  // stepper through a ref rather than by rebinding on every render the cursor
  // causes.
  const stepRef = useRef(onStep);
  useEffect(() => {
    stepRef.current = onStep;
  });

  useEffect(() => {
    if (!enabled) return;
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target !== null && /^(INPUT|TEXTAREA)$/.test(target.tagName)) return;
      if (event.key === 'ArrowLeft') stepRef.current(-1);
      if (event.key === 'ArrowRight') stepRef.current(1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enabled]);

  return null;
}
